import React, { useState, useEffect, useMemo } from 'react';
import { doc, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/config/firebase';
import { University } from '../../types';
import { universityService } from '../../services/universityService';
import {
  GraduationCap,
  Plus,
  Search,
  Filter,
  MapPin,
  Building2,
  CheckCircle2,
  XCircle,
  Edit2,
  RefreshCw,
  Power,
  X,
  Trash2,
} from 'lucide-react';
import { AdminLoadMore } from './AdminLoadMore';
import './AdminPages.css';

export const AdminUniversities: React.FC = () => {
  const [universities, setUniversities] = useState<University[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [govFilter, setGovFilter] = useState('ALL');
  const [visibleCount, setVisibleCount] = useState<number>(15);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUni, setEditingUni] = useState<University | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);

  // Form state
  const [formName, setFormName] = useState('');
  const [formGov, setFormGov] = useState('القاهرة');
  const [formCity, setFormCity] = useState('');
  const [formCampus, setFormCampus] = useState('');
  const [formAddress, setFormAddress] = useState('');
  const [formActive, setFormActive] = useState(true);

  // Load universities
  const loadUniversities = async () => {
    setIsLoading(true);
    try {
      const list = await universityService.getAllUniversities();
      setUniversities(list);
    } catch (err) {
      console.warn('Falling back to local universities catalog:', err);
      const list = await universityService.getAllUniversities();
      setUniversities(list);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadUniversities();
  }, []);

  // Governorates list
  const governorates = useMemo(() => {
    const set = new Set(universities.map((u) => u.governorate).filter(Boolean));
    return Array.from(set);
  }, [universities]);

  // Filtered list
  const filteredUniversities = useMemo(() => {
    return universities.filter((u) => {
      const matchesSearch =
        searchTerm === '' ||
        u.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.governorate?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.city?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.campus?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesGov = govFilter === 'ALL' || u.governorate === govFilter;
      return matchesSearch && matchesGov;
    });
  }, [universities, searchTerm, govFilter]);

  // Reset pagination on filter change
  useEffect(() => {
    setVisibleCount(15);
  }, [searchTerm, govFilter]);

  const visibleUniversities = useMemo(() => {
    return filteredUniversities.slice(0, visibleCount);
  }, [filteredUniversities, visibleCount]);


  // Stats
  const stats = useMemo(() => {
    const total = universities.length;
    const active = universities.filter((u) => u.active !== false && u.isActive !== false).length;
    const inActive = total - active;
    const govs = governorates.length;
    return { total, active, inActive, govs };
  }, [universities, governorates]);

  // Open modal for Create
  const handleOpenCreate = () => {
    setEditingUni(null);
    setFormName('');
    setFormGov('القاهرة');
    setFormCity('');
    setFormCampus('');
    setFormAddress('');
    setFormActive(true);
    setIsModalOpen(true);
  };

  // Open modal for Edit
  const handleOpenEdit = (uni: University) => {
    setEditingUni(uni);
    setFormName(uni.name);
    setFormGov(uni.governorate);
    setFormCity(uni.city || '');
    setFormCampus(uni.campus || '');
    setFormAddress(uni.address || '');
    setFormActive(uni.active !== false && uni.isActive !== false);
    setIsModalOpen(true);
  };

  // Toggle active status
  const handleToggleActive = async (uni: University) => {
    const nextActive = !(uni.active !== false && uni.isActive !== false);
    setActionLoading(true);

    try {
      const docRef = doc(db, 'universities', uni.id);
      await updateDoc(docRef, {
        active: nextActive,
        isActive: nextActive,
        updatedAt: new Date().toISOString(),
      });

      setUniversities((prev) =>
        prev.map((u) => (u.id === uni.id ? { ...u, active: nextActive, isActive: nextActive } : u))
      );

      setMessage({
        text: `تم ${nextActive ? 'تفعيل' : 'إلغاء تفعيل'} استقبال حجوزات ${uni.name} بنجاح.`,
        type: 'success',
      });
    } catch (err: any) {
      // Local fallback in dev
      setUniversities((prev) =>
        prev.map((u) => (u.id === uni.id ? { ...u, active: nextActive, isActive: nextActive } : u))
      );
      setMessage({
        text: `تم تحديث حالة الجامعة محلياً إلى ${nextActive ? 'مفعلة' : 'معطلة'}.`,
        type: 'success',
      });
    } finally {
      setActionLoading(false);
      setTimeout(() => setMessage(null), 4000);
    }
  };

  // Delete university cleanly from database
  const handleDeleteUniversity = async (uni: University) => {
    if (!window.confirm(`هل أنت متأكد من حذف جامعة "${uni.name}" نهائياً من قاعدة البيانات والمنظومة؟ سيتم إزالة كافة بياناتها.`)) {
      return;
    }
    setActionLoading(true);
    try {
      await universityService.deleteUniversity(uni.id);
      setUniversities((prev) => prev.filter((u) => u.id !== uni.id));
      setMessage({
        text: `تم حذف جامعة "${uni.name}" نهائياً من قاعدة البيانات بنجاح!`,
        type: 'success',
      });
    } catch (err: any) {
      console.warn('University delete fallback:', err);
      universityService.markUniversityDeleted(uni.id);
      setUniversities((prev) => prev.filter((u) => u.id !== uni.id));
      setMessage({ text: `تم حذف جامعة "${uni.name}" محلياً.`, type: 'success' });
    } finally {
      setActionLoading(false);
      setTimeout(() => setMessage(null), 4000);
    }
  };

  // Save (Create or Update)
  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formName.trim()) {
      alert('يرجى إدخال اسم الجامعة');
      return;
    }

    setActionLoading(true);
    const nowIso = new Date().toISOString();

    try {
      if (editingUni) {
        // Update
        const docRef = doc(db, 'universities', editingUni.id);
        const updated: University = {
          ...editingUni,
          name: formName.trim(),
          governorate: formGov,
          city: formCity.trim(),
          campus: formCampus.trim(),
          address: formAddress.trim(),
          active: formActive,
          isActive: formActive,
          updatedAt: nowIso,
        };

        await setDoc(docRef, updated, { merge: true });
        setUniversities((prev) => prev.map((u) => (u.id === editingUni.id ? updated : u)));
        setMessage({ text: 'تم تحديث بيانات الجامعة بنجاح.', type: 'success' });
      } else {
        // Create
        const id = 'uni_' + Math.random().toString(36).substring(2, 8);
        const newUni: University = {
          id,
          name: formName.trim(),
          governorate: formGov,
          city: formCity.trim(),
          campus: formCampus.trim(),
          address: formAddress.trim(),
          active: formActive,
          isActive: formActive,
          createdAt: nowIso,
          updatedAt: nowIso,
        };

        const docRef = doc(db, 'universities', id);
        await setDoc(docRef, newUni, { merge: true });
        setUniversities((prev) => [newUni, ...prev]);
        setMessage({ text: 'تمت إضافة الجامعة بنجاح.', type: 'success' });
      }

      setIsModalOpen(false);
    } catch (err: any) {
      // Local fallback
      if (editingUni) {
        setUniversities((prev) =>
          prev.map((u) =>
            u.id === editingUni.id
              ? {
                  ...u,
                  name: formName,
                  governorate: formGov,
                  city: formCity,
                  campus: formCampus,
                  address: formAddress,
                  active: formActive,
                  isActive: formActive,
                }
              : u
          )
        );
      } else {
        const id = 'uni_' + Date.now();
        setUniversities((prev) => [
          {
            id,
            name: formName,
            governorate: formGov,
            city: formCity,
            campus: formCampus,
            address: formAddress,
            active: formActive,
            isActive: formActive,
            createdAt: nowIso,
            updatedAt: nowIso,
          },
          ...prev,
        ]);
      }
      setIsModalOpen(false);
      setMessage({ text: 'تم حفظ بيانات الجامعة محلياً.', type: 'success' });
    } finally {
      setActionLoading(false);
      setTimeout(() => setMessage(null), 4000);
    }
  };

  return (
    <div className="admin-page-container" dir="rtl">
      {/* Header */}
      <div className="admin-page-header">
        <div className="admin-header-title-box">
          <h2>
            <GraduationCap size={24} className="text-[#E5232A]" />
            <span>الجامعات والكليات المدعومة</span>
          </h2>
          <p>إدارة شبكة الجامعات المصرية، نقاط النزول، وتفعيل وتعطيل خطوط السفر لجدول الطلاب.</p>
        </div>

        <div className="admin-header-actions-row">
          <button type="button" className="btn-admin-primary" onClick={handleOpenCreate}>
            <Plus size={18} />
            <span>إضافة جامعة جديدة</span>
          </button>
          <button type="button" className="btn-admin-outline" onClick={loadUniversities} disabled={isLoading}>
            <RefreshCw size={16} className={isLoading ? 'animate-spin' : ''} />
            <span>تحديث</span>
          </button>
        </div>
      </div>

      {/* Message alert */}
      {message && (
        <div className={`p-3 rounded-xl border flex items-center gap-2 ${message.type === 'success' ? 'bg-emerald-50 text-emerald-800 border-emerald-200' : 'bg-red-50 text-red-800 border-red-200'}`}>
          <CheckCircle2 size={18} />
          <span>{message.text}</span>
        </div>
      )}

      {/* Stats */}
      <div className="admin-stats-strip">
        <div className="admin-stat-card">
          <div className="stat-icon blue">
            <GraduationCap size={22} />
          </div>
          <div className="stat-meta">
            <span className="stat-val font-mono">{stats.total}</span>
            <span className="stat-lbl">إجمالي الجامعات</span>
          </div>
        </div>

        <div className="admin-stat-card">
          <div className="stat-icon green">
            <CheckCircle2 size={22} />
          </div>
          <div className="stat-meta">
            <span className="stat-val font-mono">{stats.active}</span>
            <span className="stat-lbl">جامعات مفعلة للحجز</span>
          </div>
        </div>

        <div className="admin-stat-card">
          <div className="stat-icon red">
            <XCircle size={22} />
          </div>
          <div className="stat-meta">
            <span className="stat-val font-mono">{stats.inActive}</span>
            <span className="stat-lbl">جامعات معطلة مؤقتًا</span>
          </div>
        </div>

        <div className="admin-stat-card">
          <div className="stat-icon purple">
            <MapPin size={22} />
          </div>
          <div className="stat-meta">
            <span className="stat-val font-mono">{stats.govs}</span>
            <span className="stat-lbl">محافظات مغطاة</span>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="admin-toolbar-card">
        <div className="admin-search-wrapper">
          <Search size={18} />
          <input
            type="text"
            className="admin-search-input"
            placeholder="البحث باسم الجامعة، المحافظة، الحرم الجامعي..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="admin-filter-group">
          <Filter size={16} className="text-slate-400" />
          <select
            className="admin-filter-select"
            value={govFilter}
            onChange={(e) => setGovFilter(e.target.value)}
          >
            <option value="ALL">جميع المحافظات</option>
            {governorates.map((g) => (
              <option key={g} value={g}>
                {g}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Universities Cards Grid */}
      {isLoading ? (
        <div className="table-empty-state bg-white rounded-2xl border p-12">
          <RefreshCw size={32} className="animate-spin text-[#E5232A]" />
          <p>جاري تحميل بيانات الجامعات...</p>
        </div>
      ) : filteredUniversities.length === 0 ? (
        <div className="table-empty-state bg-white rounded-2xl border p-12">
          <GraduationCap size={40} />
          <p>لا توجد جامعات تطابق معايير البحث الحالية.</p>
        </div>
      ) : (
        <>
          <div className="admin-cards-grid">
            {visibleUniversities.map((uni) => {
              const isActive = uni.active !== false && uni.isActive !== false;
              return (
                <div key={uni.id} className="admin-item-card">
                  <div className="admin-item-top">
                    <div className="item-icon-title">
                      <div className="w-10 h-10 rounded-xl bg-rose-50 text-[#E5232A] flex items-center justify-center font-bold">
                        <GraduationCap size={20} />
                      </div>
                      <div>
                        <strong>{uni.name}</strong>
                        <span className="text-xs text-slate-500 block">
                          محافظة {uni.governorate} — {uni.city || 'المدينة'}
                        </span>
                      </div>
                    </div>

                    <span className={`status-pill ${isActive ? 'active' : 'cancelled'}`}>
                      {isActive ? 'مفعلة' : 'معطلة'}
                    </span>
                  </div>

                  <div className="admin-item-meta">
                    <div className="flex items-center gap-1.5">
                      <Building2 size={14} className="text-slate-400" />
                      <span>الحرم: <strong>{uni.campus || 'الحرم الرئيسي'}</strong></span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <MapPin size={14} className="text-slate-400" />
                      <span className="truncate">{uni.address || 'العنوان الرئيسي للجامعة'}</span>
                    </div>
                  </div>

                  <div className="admin-item-actions">
                    <button
                      type="button"
                      className="btn-admin-outline"
                      onClick={() => handleOpenEdit(uni)}
                    >
                      <Edit2 size={14} />
                      <span>تعديل</span>
                    </button>

                    <button
                      type="button"
                      className={isActive ? 'btn-admin-danger' : 'btn-admin-success'}
                      onClick={() => handleToggleActive(uni)}
                      disabled={actionLoading}
                      title={isActive ? 'تعطيل الحجوزات لهذه الجامعة' : 'تفعيل الحجوزات لهذه الجامعة'}
                    >
                      <Power size={14} />
                      <span>{isActive ? 'تعطيل' : 'تفعيل'}</span>
                    </button>

                    <button
                      type="button"
                      className="btn-admin-danger"
                      style={{ background: '#FEE2E2', color: '#DC2626', borderColor: '#FCA5A5' }}
                      onClick={() => handleDeleteUniversity(uni)}
                      disabled={actionLoading}
                      title="حذف الجامعة نهائياً من قاعدة البيانات"
                    >
                      <Trash2 size={14} />
                      <span>حذف</span>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {filteredUniversities.length > 0 && (
            <AdminLoadMore
              currentCount={Math.min(visibleCount, filteredUniversities.length)}
              totalCount={filteredUniversities.length}
              itemName="جامعة"
              onLoadMore={() => setVisibleCount((prev) => prev + 10)}
              step={10}
              onShowAll={() => setVisibleCount(filteredUniversities.length)}
            />
          )}
        </>
      )}

      {/* Add / Edit University Modal */}
      {isModalOpen && (
        <div className="admin-modal-overlay" onClick={() => setIsModalOpen(false)}>
          <div className="admin-modal-card" onClick={(e) => e.stopPropagation()}>
            <form onSubmit={handleSave}>
              <div className="admin-modal-header">
                <h3>{editingUni ? `تعديل جامعة: ${editingUni.name}` : 'إضافة جامعة جديدة للنظام'}</h3>
                <button
                  type="button"
                  className="admin-modal-close-btn"
                  onClick={() => setIsModalOpen(false)}
                >
                  <X size={20} />
                </button>
              </div>

              <div className="admin-modal-body">
                <div className="admin-form-group">
                  <label>اسم الجامعة *</label>
                  <input
                    type="text"
                    required
                    placeholder="مثال: جامعة المنصورة"
                    value={formName}
                    onChange={(e) => setFormName(e.target.value)}
                  />
                </div>

                <div className="form-grid-2">
                  <div className="admin-form-group">
                    <label>المحافظة *</label>
                    <input
                      type="text"
                      required
                      placeholder="مثال: الدقهلية"
                      value={formGov}
                      onChange={(e) => setFormGov(e.target.value)}
                    />
                  </div>

                  <div className="admin-form-group">
                    <label>المدينة / الحي</label>
                    <input
                      type="text"
                      placeholder="مثال: المنصورة"
                      value={formCity}
                      onChange={(e) => setFormCity(e.target.value)}
                    />
                  </div>
                </div>

                <div className="admin-form-group">
                  <label>اسم الحرم الجامعي / البوابة</label>
                  <input
                    type="text"
                    placeholder="مثال: المقر الرئيسي - بين السرايات"
                    value={formCampus}
                    onChange={(e) => setFormCampus(e.target.value)}
                  />
                </div>

                <div className="admin-form-group">
                  <label>العنوان التفصيلي / نقطة الإنزال للسيارات</label>
                  <input
                    type="text"
                    placeholder="مثال: شارع الجمهورية - أمام بوابة كلية الطب"
                    value={formAddress}
                    onChange={(e) => setFormAddress(e.target.value)}
                  />
                </div>

                <div className="flex items-center gap-2 pt-2">
                  <input
                    type="checkbox"
                    id="uni-active-check"
                    checked={formActive}
                    onChange={(e) => setFormActive(e.target.checked)}
                    className="w-4 h-4 accent-[#E5232A]"
                  />
                  <label htmlFor="uni-active-check" className="text-sm font-bold text-slate-800 cursor-pointer">
                    تفعيل استقبال حجوزات الطلاب لهذه الجامعة فوراً
                  </label>
                </div>
              </div>

              <div className="admin-modal-footer">
                <button
                  type="button"
                  className="btn-admin-outline"
                  onClick={() => setIsModalOpen(false)}
                >
                  إلغاء
                </button>
                <button
                  type="submit"
                  className="btn-admin-primary"
                  disabled={actionLoading}
                >
                  <CheckCircle2 size={16} />
                  <span>{editingUni ? 'حفظ التعديلات' : 'إضافة الجامعة'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
